/**
 * Peso (kg) por catalogId — usado para calcular el costo de envío en el servidor
 * (ver `packages/shared/src/shipping.ts`) y en la estimación del carrito (apps/web).
 *
 * El peso de un producto/servicio base nunca cambia por sí solo (`BASE_WEIGHTS_KG`).
 * El de un tratamiento es constante y se define aquí EXPLÍCITO, no se recalcula en cada
 * carga a partir de qué productos lleva — así solo hay un lugar (este archivo) donde vive
 * el peso real de cada paquete, en vez de repetir la composición en dos sitios.
 *
 * Si cambias qué productos (o cuántos) lleva un tratamiento en
 * apps/web/src/content/treatments.ts, debes recalcular y actualizar aquí a mano el peso
 * de ese `catalogId`: suma `BASE_WEIGHTS_KG[producto] * cantidad` por cada producto del
 * paquete.
 */

/** Peso en kg de cada producto/servicio base, por catalogId. */
export const BASE_WEIGHTS_KG: Record<string, number> = {
	"shalosh-bayom": 1.27,
	dermogel: 0.305,
	taftefet: 0.09,
	"agua-vita": 1,
	lazihum: 0.16,
	nazelet: 0.24,
	"jabon-neutro": 0.16,
	te: 0.025,
	consulta: 0,
	dieta: 0
}

/** Peso en kg de cada paquete de tratamiento (Básico/Completo), constante y explícito. */
export const TREATMENT_WEIGHTS_KG: Record<string, number> = {
	"acidez-basico": 7.27,
	"acidez-completo": 14.54,
	"acne-basico": 0.625,
	"acne-completo": 7.895,
	"ansiedad-basico": 1.665,
	"ansiedad-completo": 7.825,
	"artritis-basico": 4.575,
	"artritis-completo": 11.845,
	"amenorrea-basico": 4.675,
	"amenorrea-completo": 9.415,
	"asma-basico": 4.36,
	"asma-completo": 7.52,
	"bronquitis-basico": 5.46,
	"bronquitis-completo": 9.62,
	"cancer-basico": 5.675,
	"cancer-completo": 11.185,
	"cataratas-basico": 1.36,
	"cataratas-completo": 8.63,
	"conjuntivitis-basico": 0.41,
	"conjuntivitis-completo": 0.715,
	"dermatitis-basico": 1.895,
	"dermatitis-completo": 6.165,
	"diabetes-basico": 6.485,
	"diabetes-completo": 9.185,
	"diabetes-tipo-2-basico": 4.65,
	"diabetes-tipo-2-completo": 9.245,
	"envejecimiento-prematuro-basico": 1.735,
	"envejecimiento-prematuro-completo": 8.165,
	"estrenimiento-basico": 7.42,
	"estrenimiento-completo": 11.095,
	"heridas-basico": 0.465,
	"heridas-completo": 4.895,
	"menopausia-basico": 4.65,
	"menopausia-completo": 7.725,
	"parkinson-basico": 5.675,
	"parkinson-completo": 11.345,
	"prostatitis-basico": 4.345,
	"prostatitis-completo": 9.095,
	"sobrepeso-basico": 5.37,
	"sobrepeso-completo": 10.79,
	"vph-basico": 1.895,
	"vph-completo": 6.47
}

/** Peso en kg de un catalogId (producto/servicio base, o tratamiento). 0 si no se conoce. */
export function getCatalogWeightKg(catalogId: string): number {
	return BASE_WEIGHTS_KG[catalogId] ?? TREATMENT_WEIGHTS_KG[catalogId] ?? 0
}
